import { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { VendorState } from "../../../../redux/vendor/rootReducer";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OpusRequirements } from "../models/OpusRequirements.model";
import { useTextureApi } from "../../../../common/hooks/use-texture-api.hook";
import { setLoading } from "../../../../redux/vendor/ui/uiActions";
import {
    GroutColorField,
    PatternFieldLabel,
    PatternSectionTitle,
    SliderWithInput,
} from "./PatternFormControls";

// Add interface for component props
interface OpusPatternFormProps {
  seamlessResult: any;
  setSeamlessResult: any;
}

// One tile per opus stone — the same upload square repeats four times with
// only the image slot differing.
const UploadTile = ({ image, inputRef, onPick, onFileChange, onRemove, label, browseLabel, alt }) => (
  <div
    onClick={onPick}
    className="flex h-[120px] cursor-pointer flex-col items-center justify-center border border-dashed border-border bg-muted/40 p-3 text-center transition-colors hover:border-foreground/40 hover:bg-muted"
  >
    {!image ? (
      <>
        <span className="mb-1 text-xs text-muted-foreground">{label}</span>
        <span className="text-xs text-muted-foreground/70">{browseLabel}</span>
      </>
    ) : (
      <div className="relative h-full w-full">
        <img
          src={URL.createObjectURL(image)}
          alt={alt}
          className="h-full w-full object-cover"
        />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -end-2.5 -top-2.5 flex size-6 items-center justify-center rounded-full border border-border bg-background text-muted-foreground transition-colors hover:border-destructive hover:bg-destructive hover:text-white"
        >
          <X className="size-3.5" />
        </button>
      </div>
    )}
    <input
      type="file"
      onChange={onFileChange}
      accept="image/*"
      ref={inputRef}
      className="hidden"
    />
  </div>
);

export const OpusPatternForm = ({seamlessResult, setSeamlessResult}: OpusPatternFormProps) => {
    const { t } = useTranslation();
    const hiddenFile1Input = useRef(null);
    const hiddenFile2Input = useRef(null);
    const hiddenFile3Input = useRef(null);
    const hiddenFile4Input = useRef(null);
    const { language } = useSelector((state: VendorState) => state.settings.theme);
    const [image1, setImage1] = useState<any>()
    const [image2, setImage2] = useState<any>()
    const [image3, setImage3] = useState<any>()
    const [image4, setImage4] = useState<any>()
    const [isOnEditGroutColor, setIsOnEditGroutColor] = useState(false)
    const textureAPI = useTextureApi()
    const dispatch = useDispatch()
    const [fields, setFields] = useState({
        textureWidth: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.TEXTURE_WIDTH')}`,
            name: "textureWidth",
      value: "2",
      unit: "m"
        },
        textureHeight: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.TEXTURE_HEIGHT')}`,
            name: "textureHeight",
      value: "2",
      unit: "m"
        },
    groutLine: {
            isTouched: false,
      label: `${t('PATTERNS.PARTIALS.GROUT_LINE_WIDTH')}`,
      name: "groutLine",
      value: "2",
      unit: "mm"
    },
    groutColor: {
            isTouched: false,
            label: `${t('PATTERNS.PARTIALS.GROUT_LINE_COLOR')}`,
      name: "groutColor",
      value: "#CCCCCC"
    }
  });

    const handleClick = (event, number) => {
        if (number === 1)
            hiddenFile1Input.current.click();
        else if (number === 2)
            hiddenFile2Input.current.click();
        else if (number === 3)
            hiddenFile3Input.current.click();
        else if (number === 4)
            hiddenFile4Input.current.click();
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
    setFields({...fields, [name]: {
                ...fields[name],
                value: value,
                isTouched: true
    }});
  };

  const handleSliderChange = (name, value) => {
    setFields({...fields, [name]: {
      ...fields[name],
      value: value.toString(),
      isTouched: true
    }});
  };

  const isValidForm = () => {
    return image1 && image2 && image3 && image4 &&
      fields.textureWidth.value &&
      fields.textureHeight.value;
  };

    const removeImage = (indexOfImageToRemove) => {
        if (indexOfImageToRemove === 1) {
            setImage1(null)
            hiddenFile1Input.current.value = ""
        }
        if (indexOfImageToRemove === 2) {
            setImage2(null)
            hiddenFile2Input.current.value = ""
        }
        if (indexOfImageToRemove === 3) {
            setImage3(null)
            hiddenFile3Input.current.value = ""
        }
        if (indexOfImageToRemove === 4) {
            setImage4(null)
            hiddenFile4Input.current.value = ""
        }
  };

    const handleGenerateSeamless = async () => {
    if (isValidForm()) {
      try {
        dispatch(setLoading(true))
        const body = new OpusRequirements()
        body.setImageA(image1)
            .setImageB(image2)
            .setImageC(image3)
            .setImageD(image4)
            .setTextureWidth(fields.textureWidth.value)
            .setTextureHeight(fields.textureHeight.value)
            .setGroutLineColor(fields.groutColor.value || "#CCCCCC")
            .setGroutLineWidth(fields.groutLine.value)
        let data = await textureAPI.generateTextureForPattern(body, "opus");
        setSeamlessResult(data)
      } catch (error) {
        console.error('Error generating seamless texture:', error);
        setSeamlessResult(null);
      } finally {
        dispatch(setLoading(false))
      }
    }
  };

    const handleGroutColorChange = (color) => {
        setFields({
            ...fields,
      groutColor: {
        ...fields.groutColor,
        value: color.hex + Math.round(color.rgb.a * 255).toString(16)
      }
    });
  };

    return (
    <div>
      {/* Hidden generate button that can be triggered programmatically */}
      <button
        type="button"
        id="opus-generate-btn"
        onClick={handleGenerateSeamless}
        className="hidden"
      />

      <div className="mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.IMAGES')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          <UploadTile
            image={image1}
            inputRef={hiddenFile1Input}
            onPick={(e) => handleClick(e, 1)}
            onFileChange={(event) => setImage1(event.target.files[0])}
            onRemove={() => removeImage(1)}
            label={t('PATTERNS.PARTIALS.UPLOAD_IMAGE_1')}
            browseLabel={t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
            alt="Image 1"
          />
          <UploadTile
            image={image2}
            inputRef={hiddenFile2Input}
            onPick={(e) => handleClick(e, 2)}
            onFileChange={(event) => setImage2(event.target.files[0])}
            onRemove={() => removeImage(2)}
            label={t('PATTERNS.PARTIALS.UPLOAD_IMAGE_2')}
            browseLabel={t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
            alt="Image 2"
          />
          <UploadTile
            image={image3}
            inputRef={hiddenFile3Input}
            onPick={(e) => handleClick(e, 3)}
            onFileChange={(event) => setImage3(event.target.files[0])}
            onRemove={() => removeImage(3)}
            label={t('PATTERNS.PARTIALS.UPLOAD_IMAGE_3')}
            browseLabel={t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
            alt="Image 3"
          />
          <UploadTile
            image={image4}
            inputRef={hiddenFile4Input}
            onPick={(e) => handleClick(e, 4)}
            onFileChange={(event) => setImage4(event.target.files[0])}
            onRemove={() => removeImage(4)}
            label={t('PATTERNS.PARTIALS.UPLOAD_IMAGE_4')}
            browseLabel={t('PATTERNS.PARTIALS.CLICK_TO_BROWSE')}
            alt="Image 4"
          />
        </div>
      </div>

      <div className="mt-8 mb-6">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.TEXTURE')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.textureWidth.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureWidth"
              value={fields.textureWidth.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.textureHeight.label}</PatternFieldLabel>
            <SliderWithInput
              name="textureHeight"
              value={fields.textureHeight.value}
              min={1}
              max={4}
              step={1}
              markLabels={['1m', '2m', '3m', '4m']}
              showInput={false}
              onSliderChange={handleSliderChange}
            />
          </div>
        </div>
      </div>

      <div className="mt-8">
        <PatternSectionTitle>{t('PATTERNS.PARTIALS.GROUT')}</PatternSectionTitle>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PatternFieldLabel>{fields.groutLine.label}</PatternFieldLabel>
            <SliderWithInput
              name="groutLine"
              value={fields.groutLine.value}
              unit={fields.groutLine.unit}
              min={0}
              max={10}
              step={0.5}
              onSliderChange={handleSliderChange}
              onInputChange={handleChange}
            />
          </div>

          <div>
            <PatternFieldLabel>{fields.groutColor.label}</PatternFieldLabel>
            <GroutColorField
              color={fields.groutColor.value}
              open={isOnEditGroutColor}
              onOpenChange={setIsOnEditGroutColor}
              onColorChange={handleGroutColorChange}
            />
          </div>
        </div>
      </div>
    </div>
    );
};
